import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { useNavigate } from "react-router-dom";
import { IntegrationStatusPill } from "../components/shared/IntegrationHealth";
import { Search, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";

const CAT_COLORS = {
  engineering: "#2563EB",
  plm: "#8B5CF6",
  eam: "#10B981",
  mes: "#D97706",
  identity: "#DC2626",
  workflow: "#0EA5E9",
  project: "#F97316",
  digital_twin: "#6366F1",
};

export default function ConnectorsListPage({ onLogout, userRole, isFeatureEnabled }) {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [statuses, setStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const fetchAll = useCallback(async () => {
    try {
      const [catR, statR] = await Promise.all([
        axios.get(`${API}/connectors/catalog`, { headers: getAuthHeaders() }),
        axios.get(`${API}/connectors/status`, { headers: getAuthHeaders() }),
      ]);
      setCatalog(catR.data);
      setStatuses(statR.data || {});
    } catch { toast.error("Failed to load connectors"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading || !catalog) return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64 text-[#64748B] font-mono">Loading connectors...</div></Layout>;

  const q = search.toLowerCase();
  const activeCount = Object.values(statuses).filter(s => s === "active").length;
  const configuredCount = Object.values(statuses).filter(s => s === "configured").length;

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="connectors-list">
        <PageHeader title="Connectors" subtitle={`${catalog.total} available · ${activeCount} active · ${configuredCount} configured`} testId="connectors-title" />

        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search connectors..." className="pl-10 rounded-none border-[#E2E8F0]" data-testid="connector-search" />
        </div>

        {Object.entries(catalog.categories).map(([catKey, cat]) => {
          const filtered = cat.connectors.filter(c => !q || c.name.toLowerCase().includes(q) || c.description.toLowerCase().includes(q));
          if (!filtered.length) return null;
          const color = CAT_COLORS[catKey] || "#64748B";
          return (
            <div key={catKey}>
              <h2 className="font-heading font-bold text-lg text-[#0F172A] mb-3 flex items-center gap-2">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                {cat.label}
                <span className="text-xs text-[#94A3B8] font-mono">({filtered.length})</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {filtered.map(c => {
                  const status = statuses[c.key] || "not_configured";
                  return (
                    <button
                      key={c.key}
                      onClick={() => navigate(`/connectors/${c.key}`)}
                      className="text-left p-4 bg-white border border-[#E2E8F0] hover:border-[#0F172A] transition-colors group"
                      data-testid={`connector-card-${c.key}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-bold text-sm text-[#0F172A] group-hover:text-[#2563EB] transition-colors">{c.name}</h3>
                        <ChevronRight size={16} className="text-[#CBD5E1] group-hover:text-[#2563EB] transition-colors" />
                      </div>
                      <p className="text-xs text-[#64748B] leading-relaxed mb-3">{c.description}</p>
                      <div className="flex items-center justify-between">
                        <IntegrationStatusPill status={status} />
                        <span className="text-[10px] text-[#94A3B8] font-mono uppercase">{c.auth_type.replace("_", " ")}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </Layout>
  );
}
